/**
 * @fileoverview Content Reporting Hook
 *
 * Provides functionality for users to report inappropriate content
 * (polls or comments) for moderation review.
 *
 * @module hooks/useReports
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

/**
 * Available report reasons.
 * - spam: Unwanted promotional content
 * - harassment: Bullying or targeted abuse
 * - inappropriate: Offensive or NSFW content
 * - other: Catch-all for other violations
 */
export type ReportReason = 'spam' | 'harassment' | 'inappropriate' | 'other';

/**
 * Hook for reporting polls and comments for moderation.
 *
 * Reports are stored in the `reports` table for admin review.
 * Users must be authenticated to submit reports.
 *
 * @returns {Object} Report methods and state
 * @property {Function} reportPoll - Report a poll
 * @property {Function} reportComment - Report a comment
 * @property {boolean} isSubmitting - True while report is being submitted
 *
 * @example
 * const { reportPoll, isSubmitting } = useReports();
 *
 * await reportPoll(pollId, 'spam', 'This is promotional content');
 */
export function useReports() {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Report a poll for moderation.
   *
   * @param pollId - ID of the poll to report
   * @param reason - Category of the report
   * @param description - Optional detailed explanation
   * @returns True if report was submitted successfully
   */
  const reportPoll = async (pollId: string, reason: ReportReason, description?: string) => {
    if (!user) {
      toast.error('Please sign in to report content');
      return false;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: user.id,
          poll_id: pollId,
          reason,
          description: description?.trim() || null
        });

      if (error) throw error;

      toast.success('Report submitted. Thank you for helping keep our community safe.');
      return true;
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Report a comment for moderation.
   *
   * @param commentId - ID of the comment to report
   * @param reason - Category of the report
   * @param description - Optional detailed explanation
   * @returns True if report was submitted successfully
   */
  const reportComment = async (commentId: string, reason: ReportReason, description?: string) => {
    if (!user) {
      toast.error('Please sign in to report content');
      return false;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: user.id,
          comment_id: commentId,
          reason,
          description: description?.trim() || null
        });

      if (error) throw error;

      toast.success('Report submitted. Thank you for helping keep our community safe.');
      return true;
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    reportPoll,
    reportComment,
    isSubmitting
  };
}
